import {
  BillingQuoteInputSchema,
  type BillingQuote,
  type BillingQuoteInput,
  type BillingQuoteUpdate,
  type BillingTerms,
  type BillingTermsInput,
  type BillingTermsUpdate,
} from "../../shared/billing-schemas";
import type { BillingSignedDocument } from "../../shared/billing-document-schemas";
import { activeTermsFor } from "./billing-agreements";
import {
  assembleTerms,
  buildTermsInput,
  draftFrom,
  mergeErrors,
  termsFieldName,
  termsGuide,
  type CommercialSource,
  type TermsDraft,
} from "./billing-terms-draft";
import type { FieldErrors, FieldGuide } from "./billing-validation";

/**
 * What the quote form holds while an employee types (ADR-0075), and how it becomes the input the
 * server parses.
 *
 * A quote is a terms draft plus what the customer needs to decide: a message written for them and
 * the billing contacts they named. The commercial half is built by the terms builder, so a quote's
 * lines and totals are validated by exactly the code that validates the terms it becomes.
 */

export type ContactDraft = { key: number; name: string; email: string };

/** `approveNow` (ADR-0077): the employee vouches for the terms and nothing is sent. */
export type QuoteDraft = TermsDraft & {
  message: string;
  contacts: ContactDraft[];
  approveNow: boolean;
};

let contactKeys = 0;

export function blankContact(name = "", email = ""): ContactDraft {
  contactKeys += 1;
  return { key: contactKeys, name, email };
}

/** What a draft is pre-filled from: a quote, or the terms a quote is replacing. */
export type QuoteSource = CommercialSource & {
  message?: string | null;
  contacts?: readonly { name: string; email: string }[];
};

/** A blank draft, or one pre-filled from an existing quote or version for editing or replacing. */
export function quoteDraftFrom(initial: QuoteSource | null, approveNow = false): QuoteDraft {
  if (initial === null) return { ...draftFrom(null), message: "", contacts: [], approveNow: false };
  return {
    ...draftFrom(initial),
    message: initial.message ?? "",
    contacts: (initial.contacts ?? []).map((contact) => blankContact(contact.name, contact.email)),
    approveNow,
  };
}

const QUOTE_REQUIREMENTS: Record<string, string> = {
  message:
    "Optional, up to 2000 characters. The customer reads it on their Billing tab and in the email.",
  notes: "Optional, up to 4000 characters. The customer never sees it.",
};

const CONTACT_REQUIREMENTS: Record<string, string> = {
  name: "1 to 120 characters.",
  email: "A valid email address.",
};

/** The form's name for a contact field: the contact's own key, never its position, which changes. */
export function contactField(key: number, name: string): string {
  return `contact-${key}-${name}`;
}

/** The form's name for a schema field of the quote: contacts by their own key, the rest as terms. */
export function quoteFieldName(draft: QuoteDraft): (schemaField: string) => string {
  const terms = termsFieldName(draft);
  return (schemaField) => {
    const match = /^contacts\.(\d+)\.(\w+)$/.exec(schemaField);
    if (match?.[1] !== undefined && match[2] !== undefined) {
      return contactField(draft.contacts[Number(match[1])]?.key ?? 0, match[2]);
    }
    if (schemaField === "supersedesQuoteId" || schemaField === "contacts") return "form";
    return terms(schemaField);
  };
}

/** What each field of the quote form requires, for the lines and contacts the draft currently has. */
export function quoteGuide(draft: QuoteDraft): FieldGuide {
  const requirements = { ...termsGuide(draft).requirements, ...QUOTE_REQUIREMENTS };
  requirements["title"] = "2 to 160 characters: how the proposal is titled for the customer.";
  for (const contact of draft.contacts) {
    for (const [name, requirement] of Object.entries(CONTACT_REQUIREMENTS)) {
      requirements[contactField(contact.key, name)] = requirement;
    }
  }
  return { rename: quoteFieldName(draft), requirements };
}

/** Everything typed, as the server would read it, or everything wrong with it, by field. */
export function buildQuoteInput(
  draft: QuoteDraft,
  supersedesQuoteId: string | null,
): { input: BillingQuoteInput } | { errors: FieldErrors } {
  const { candidate, errors } = assembleTerms(draft);
  const parsed = BillingQuoteInputSchema.safeParse(
    {
      ...candidate,
      message: draft.message.trim() === "" ? null : draft.message.trim(),
      contacts: draft.contacts.map((contact) => ({
        name: contact.name.trim(),
        email: contact.email.trim(),
      })),
      supersedesQuoteId,
    },
    { reportInput: true },
  );
  if (parsed.success && Object.keys(errors).length === 0) return { input: parsed.data };
  return { errors: mergeErrors(parsed, errors, quoteFieldName(draft)) };
}

/**
 * What the one form is open for (ADR-0077). `replace` is a sent or expired quote; `revise` is the
 * active terms; `edit_terms` is a draft an employee wrote directly, before quotes existed.
 */
export type QuoteFormTarget =
  | { mode: "create" }
  /** Approved terms set up from a signed order form or change order, reviewed before saving. */
  | { mode: "document"; document: BillingSignedDocument; source: QuoteSource }
  | { mode: "edit"; quote: BillingQuote }
  | { mode: "replace"; quote: BillingQuote }
  | { mode: "revise"; terms: BillingTerms }
  | { mode: "edit_terms"; terms: BillingTerms };

export type QuoteSubmission =
  | { kind: "quote"; input: BillingQuoteInput }
  | { kind: "quote_update"; quoteId: string; input: BillingQuoteUpdate }
  | { kind: "approved_terms"; input: BillingTermsInput }
  | { kind: "document_terms"; documentId: string; input: BillingTermsInput }
  | { kind: "terms_update"; termsId: string; input: BillingTermsUpdate };

export function quoteFormTitle(target: QuoteFormTarget): string {
  switch (target.mode) {
    case "create":
      return "New quote";
    case "document":
      return `Add ${target.document.reference} to billing`;
    case "edit":
      return "Edit quote";
    case "replace":
      return `Replace “${target.quote.title}”`;
    case "revise":
      return `Replace “${target.terms.title}”`;
    case "edit_terms":
      return "Edit draft";
  }
}

export function targetSource(target: QuoteFormTarget): QuoteSource | null {
  if (target.mode === "create") return null;
  if (target.mode === "document") return target.source;
  return target.mode === "edit" || target.mode === "replace" ? target.quote : target.terms;
}

/**
 * Only a new quote, or one replacing the active terms, may be approved without the customer. A
 * quote the customer still holds is withdrawn by the one that replaces it when that is sent;
 * approving terms beside it would leave them a proposal that fails the moment they accept.
 */
export function allowsApproveNow(target: QuoteFormTarget): boolean {
  return target.mode === "create" || target.mode === "revise" || target.mode === "document";
}

function quoteUpdate(input: BillingQuoteInput): BillingQuoteUpdate {
  const { toolId, title, effectiveAt, endsAt, notes, discount, lines, reason, message, contacts } =
    input;
  return { toolId, title, effectiveAt, endsAt, notes, discount, lines, reason, message, contacts };
}

function termsUpdate(input: BillingTermsInput): BillingTermsUpdate {
  const { toolId, title, effectiveAt, endsAt, notes, discount, lines, reason } = input;
  return { toolId, title, effectiveAt, endsAt, notes, discount, lines, reason };
}

/** Terms the employee vouches for: born active, replacing the active version for the tool. */
function approved(
  draft: QuoteDraft,
  target: QuoteFormTarget,
  versions: BillingTerms[],
): { submission: QuoteSubmission } | { errors: FieldErrors } {
  const toolId = draft.toolId === "" ? null : draft.toolId;
  const parent =
    target.mode === "revise" ? target.terms.id : (activeTermsFor(versions, toolId)?.id ?? null);
  const built = buildTermsInput(draft, parent);
  if ("errors" in built) return built;
  return { submission: { kind: "approved_terms", input: built.input } };
}

/** Everything typed, as the request the target calls for, or everything wrong with it. */
export function buildSubmission(
  draft: QuoteDraft,
  target: QuoteFormTarget,
  versions: BillingTerms[],
): { submission: QuoteSubmission } | { errors: FieldErrors } {
  if (target.mode === "edit_terms") {
    const built = buildTermsInput(draft, null);
    if ("errors" in built) return built;
    const input = termsUpdate(built.input);
    return { submission: { kind: "terms_update", termsId: target.terms.id, input } };
  }
  if (target.mode === "document") {
    // The customer already signed; these terms are only ever approved, never sent as a quote.
    const built = approved(draft, target, versions);
    if ("errors" in built || built.submission.kind !== "approved_terms") return built;
    const { input } = built.submission;
    return {
      submission: { kind: "document_terms", documentId: target.document.documentId, input },
    };
  }
  if (draft.approveNow && allowsApproveNow(target)) return approved(draft, target, versions);
  const built = buildQuoteInput(draft, target.mode === "replace" ? target.quote.id : null);
  if ("errors" in built) return built;
  if (target.mode === "edit") {
    const input = quoteUpdate(built.input);
    return { submission: { kind: "quote_update", quoteId: target.quote.id, input } };
  }
  return { submission: { kind: "quote", input: built.input } };
}
